import { GamePoint, InputEventDetails, PointCalculationEnum, Team } from "@/types/types";
import { CustomEventType } from "@/events/CustomEventRegistry";
import { sharedStyles } from "@lib/sharedStyles";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import "@/components/color-picker/TeamColorSwatch";

@customElement("points-calculator-row")
export class PointsCalculatorRow extends LitElement {
	static readonly styles = [
		sharedStyles,
		css`
			.event-row {
				display: flex;
				align-items: center;
				padding: 12px;
				justify-content: space-between;
			}
			.table-cell {
				flex: 1;
				display: flex;
				flex-wrap: wrap;
			}
			.table-cell--small {
				flex: 0.25;
				display: flex;
				flex-wrap: wrap;
			}
			.table-cell--inputs {
				flex: 2;
				display: flex;
				gap: 4px;
			}
			.table-cell--inputs-horizontal {
				flex-direction: row;
				align-items: center;
			}
			.team-name-cell {
				display: flex;
				align-items: center;
				gap: 8px;
			}
			.team-color-swatch-container {
				width: 20px;
				align-self: stretch;
				flex-shrink: 0;
			}
			.table-cell--inputs-vertical {
				flex-direction: column;
				align-items: flex-start;
				gap: 8px;
			}
			.event-row:hover {
				background: #f8f9fa;
			}
			.event-row--highlight {
				animation: points-calculator-row-light-up 1s ease-out;
			}
			@keyframes points-calculator-row-light-up {
				from { background: var(--color-lemon); }
				to { background: transparent; }
			}
			@media (prefers-reduced-motion: reduce) {
				.event-row--highlight {
					animation: none;
				}
			}
			.actions {
				display: flex;
				gap: 8px;
			}
			.round-label {
				font-size: 12px;
				color: #666;
				min-width: 60px;
			}
			.time-input-row {
				display: flex;
				align-items: center;
				gap: 8px;
			}
		`
	];

	@property({ type: Object }) team: Team;
	@property({ type: String }) gameId: string;
	@property({ type: Number }) rounds: number;
	@property({ type: Object }) gamePoint?: GamePoint;
	@property({ type: String }) pointCalculationType: PointCalculationEnum;
	@property({ type: Array }) disabledRounds?: number[];
	@property({ type: Boolean }) highlight = false;

	private _getRoundPointForRound(roundIndex: number, gamePoint?: GamePoint): number | null {
		if (!gamePoint) return null;
		const roundPoint = gamePoint.rounds.find(rp => rp.round === roundIndex);
		if (roundPoint?.points === undefined || roundPoint?.points === null) {
			return null;
		}
		return roundPoint.points;
	}

	private _getFastestTimeForRound(roundIndex: number, gamePoint?: GamePoint): number {
		if (!gamePoint) return 0;
		const roundPoint = gamePoint.rounds.find(rp => rp.round === roundIndex);
		return roundPoint?.team_fastest_time_seconds ?? 0;
	}

	private _smallestDifferenceForRound(roundIndex: number, gamePoint?: GamePoint): number | null {
		if (!gamePoint) return null;
		const roundPoint = gamePoint.rounds.find(rp => rp.round === roundIndex);
		if (roundPoint?.team_smallest_difference_value === undefined || roundPoint?.team_smallest_difference_value === null) {
			return null;
		}
		return roundPoint.team_smallest_difference_value;
	}

	private _isRoundDisabled(roundIndex: number): boolean {
		return this.disabledRounds?.includes(roundIndex) ?? false;
	}

	constructor() {
		super();
		this.addEventListener("input-change", this._handleInputChange);
		this.addEventListener("time-input-change", this._handleTimeInputChange);
	}

	private _handleInputChange = (event: CustomEvent<InputEventDetails>) => {
		this._dispatchRoundPointChangeEvent(event.detail.name, event.detail.value);
	};

	private _handleTimeInputChange = (event: CustomEvent<InputEventDetails>) => {
		this._dispatchRoundPointChangeEvent(event.detail.name, event.detail.value);
	};

	private _dispatchRoundPointChangeEvent(round: string, value: string) {
		const eventKey = this.pointCalculationType === PointCalculationEnum.fastestTime
			? CustomEventType.FASTEST_TIME_CHANGE
			: this.pointCalculationType === PointCalculationEnum.smallestDifference
				? CustomEventType.SMALLEST_DIFFERENCE_CHANGE
				: CustomEventType.ROUND_POINT_CHANGE;

		this.dispatchEvent(new CustomEvent(eventKey, {
			detail: {
				gameId: this.gameId,
				teamId: this.team.id,
				name: round,
				value
			},
			bubbles: true,
			composed: true
		}));
	}

	private _renderRoundInput(roundIndex: number) {
		if (this.pointCalculationType === PointCalculationEnum.fastestTime) {
			return html`
            <div class="time-input-row">
                <span class="round-label">Runde ${roundIndex + 1}:</span>
                <time-input
                    .inputEventIdentifier=${`${this.team.id}`}
                    .name=${`round-${roundIndex}`}
                    .value=${this._getFastestTimeForRound(roundIndex, this.gamePoint)}
                    ?disabled=${this._isRoundDisabled(roundIndex)}
                ></time-input>
            </div>
        `;
		}

		if (this.pointCalculationType === PointCalculationEnum.mostPoints) {
			const points = this._getRoundPointForRound(roundIndex, this.gamePoint);
			return html`
				<increase-decrease-number
					style="width: fit-content;"
					.min=${null}
					.value=${points ?? 0}
					.inputEventIdentifier=${`${this.team.id}`}
					.label=${`Runde ${roundIndex + 1}`}
					.name=${`round-${roundIndex}`}
				></increase-decrease-number>
			`;
		}

		const value = this.pointCalculationType === PointCalculationEnum.smallestDifference
			? this._smallestDifferenceForRound(roundIndex, this.gamePoint)
			: this._getRoundPointForRound(roundIndex, this.gamePoint);

		return html`
        <basic-number-input
            style="width: 80px;"
            .inputEventIdentifier=${`${this.team.id}`}
            .name=${`round-${roundIndex}`}
            .value=${value !== null ? String(value) : ''}
            ?disabled=${this._isRoundDisabled(roundIndex)}
        ></basic-number-input>
    `;
	}

	private _getInputsContainerClass(): string {
		const baseClass = 'table-cell--inputs';
		const directionClass = this.pointCalculationType === PointCalculationEnum.fastestTime
			? 'table-cell--inputs-vertical'
			: 'table-cell--inputs-horizontal';
		return `${baseClass} ${directionClass}`;
	}

	render() {
		return html`
			<div class="event-row ${this.highlight ? 'event-row--highlight' : ''}" data-team-id="${this.team.id}">
				<div class="table-cell--small">
					<div class="text-body--small">#${this.team.team_number}</div>
				</div>
				<div class="table-cell">
					<div class="team-name-cell">
						<div class="team-color-swatch-container">
							<team-color-swatch .color=${this.team.color}></team-color-swatch>
						</div>
						<div class="text-body--small">${this.team.name}</div>
					</div>
				</div>
				<div class="${this._getInputsContainerClass()}">
					${Array.from({ length: this.rounds }, (_, i) => this._renderRoundInput(i))}
				</div>
			</div>
		`;
	}
}

export default PointsCalculatorRow;